import { useEffect, useRef, useState } from "react";

const EMPTY_COLLECTION = { type: "FeatureCollection", features: [] };

/**
 * Mirrors the historical-flood vector tileset into a GeoJSON collection with
 * one feature per location. The tileset stores one point per flood event, so
 * places hit by several floods render as stacked circles (their opacities add
 * up) and hover only ever finds the topmost event. Vector tiles can't be
 * aggregated from the client ahead of time, so instead the tiles currently
 * loaded (kept loading by the invisible historicalFloodLoaderLayer on the raw
 * source) are re-read whenever the map goes idle, deduped, and grouped by
 * coordinate.
 *
 * Each grouped feature carries:
 * - `count`: number of events at that location
 * - `events`: JSON string of every event's original properties (kept a
 *   string so it survives queryRenderedFeatures unchanged)
 */
export const useHistoricalFloodEvents = ({
  mapRef,
  enabled,
  sourceId,
  sourceLayer,
}) => {
  const [collection, setCollection] = useState(EMPTY_COLLECTION);
  const lastSignatureRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      lastSignatureRef.current = null;
      setCollection(EMPTY_COLLECTION);
      return undefined;
    }

    const map = mapRef.current?.getMap();
    if (!map) {
      return undefined;
    }

    const regroup = () => {
      if (!map.getSource(sourceId)) {
        return;
      }

      const loaded = map.querySourceFeatures(sourceId, { sourceLayer });

      // A point near a tile edge is duplicated into the neighbouring tile's
      // buffer, so the same event can come back several times — dedupe by its
      // full property set before grouping.
      const eventsByKey = new Map();
      for (const feature of loaded) {
        if (feature.geometry?.type !== "Point") {
          continue;
        }
        const properties = feature.properties || {};
        const eventKey = JSON.stringify(properties);
        if (!eventsByKey.has(eventKey)) {
          eventsByKey.set(eventKey, {
            properties,
            coordinates: feature.geometry.coordinates,
          });
        }
      }

      const groups = new Map();
      for (const [eventKey, event] of eventsByKey.entries()) {
        const locationKey = event.coordinates
          .map((value) => value.toFixed(6))
          .join(",");
        const group = groups.get(locationKey);
        if (group) {
          group.events.push({ eventKey, properties: event.properties });
        } else {
          groups.set(locationKey, {
            coordinates: event.coordinates,
            events: [{ eventKey, properties: event.properties }],
          });
        }
      }

      // querySourceFeatures gives no stable ordering, so sort groups and the
      // events inside them — identical map states must serialize identically
      // for the signature check below to catch them.
      const features = [...groups.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([, group]) => {
          const events = [...group.events]
            .sort((a, b) => a.eventKey.localeCompare(b.eventKey))
            .map((event) => event.properties);

          return {
            type: "Feature",
            geometry: { type: "Point", coordinates: group.coordinates },
            properties: {
              count: events.length,
              events: JSON.stringify(events),
            },
          };
        });

      // Updating the GeoJSON source re-triggers idle; only publish real
      // changes so regroup → render → idle → regroup doesn't loop forever.
      const signature = JSON.stringify(features);
      if (signature === lastSignatureRef.current) {
        return;
      }
      lastSignatureRef.current = signature;
      setCollection({ type: "FeatureCollection", features });
    };

    map.on("idle", regroup);
    regroup();

    return () => {
      map.off("idle", regroup);
    };
  }, [mapRef, enabled, sourceId, sourceLayer]);

  return collection;
};
